import { useCallback, useState } from 'react';
import type { Result } from '@api';
import { Failure, Success, useErrorMessage, useDatahubAPI } from '@api';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import axios from 'axios';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useTranslation } from 'react-i18next';
import type { EventData } from '@data/datahub/event/types';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useAtomCallback } from 'jotai/utils';

type APIResponse = {
  events: EventData[];
  page_size: number;
  next_page_token?: string;
};

const useRequest = () => {
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      params: URLSearchParams,
    ): Promise<Result<APIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/events?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              events: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  return request;
};

export const useEventsAPI = (): {
  loading: boolean;
  events: EventData[];
  getEvents: (vehicleId: string, params: URLSearchParams) => Promise<void>;
  getAllEvents: (vehicleId: string, params: URLSearchParams) => Promise<void>;
  getShutdownEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<void>;
  getNetworkConnectionEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<void>;
  getFOAEvents: (vehicleId: string, params: URLSearchParams) => Promise<void>;
  getStuckAlarmEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<void>;
} => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<EventData[]>([]);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const request = useRequest();

  const getEvents = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        setEvents([]);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_events', { status: 'failed' }),
          });
          setLoading(false);
          return;
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        const splitRequest = async (): Promise<void> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            // リクエストに失敗した場合
            notifyError({
              message: `${t('api.datahub.get_events', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return;
          }
          // 取得成功時
          if (res?.isSuccess()) {
            // 配列を結合
            setEvents((prevState) => prevState.concat(res.value.events));
            if (res.value.next_page_token) {
              // レスポンスに next_page_token がある場合は次のリクエストを行う
              if (params) {
                params.set('page_token', res.value.next_page_token);
              }
              await splitRequest();
            }
          } else {
            setLoading(false);
          }
        };

        await splitRequest();

        setLoading(false);
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  const doEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams, value: string) => {
      const appendParams = new URLSearchParams(params);
      appendParams.append('category', value);
      await getEvents(vehicleId, appendParams);
    },
    [getEvents],
  );

  const getAllEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      await doEvents(vehicleId, params, 'all');
    },
    [doEvents],
  );

  const getFOAEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      await doEvents(vehicleId, params, 'foa_intervention');
    },
    [doEvents],
  );

  const getShutdownEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      await doEvents(vehicleId, params, 'shutdown');
    },
    [doEvents],
  );

  const getNetworkConnectionEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      await doEvents(vehicleId, params, 'network_connection');
    },
    [doEvents],
  );

  const getStuckAlarmEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      await doEvents(vehicleId, params, 'stuck_alarm');
    },
    [doEvents],
  );

  return {
    events,
    loading,
    getEvents,
    getAllEvents,
    getShutdownEvents,
    getNetworkConnectionEvents,
    getFOAEvents,
    getStuckAlarmEvents,
  };
};

export const useSyncEventsAPI = (): {
  loading: boolean;
  getSyncEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<EventData[]>;
  getSyncAllEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<EventData[]>;
  getSyncShutdownEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<EventData[]>;
  getSyncNetworkConnectionEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<EventData[]>;
  getSyncFOAEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<EventData[]>;
  getSyncStuckAlarmEvents: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<EventData[]>;
} => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const request = useRequest();

  const getSyncEvents = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_events', { status: 'failed' }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        const splitRequest = async (
          data: EventData[] = [],
        ): Promise<EventData[]> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            // リクエストに失敗した場合
            notifyError({
              message: `${t('api.datahub.get_events', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return data;
          }
          // 取得成功時
          if (res?.isSuccess()) {
            // 配列を結合
            const combinedData = [...data, ...res.value.events];
            if (isNullOrUndefined(res.value.next_page_token)) {
              return combinedData;
            }
            // レスポンスに next_page_token がある場合は次のリクエストを行う
            params.set('page_token', res.value.next_page_token);
            return await splitRequest(combinedData);
          }
          return data;
        };

        const data = await splitRequest();
        setLoading(false);
        return data;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  const doSyncEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams, value: string) => {
      const appendParams = new URLSearchParams(params);
      appendParams.append('category', value);
      return await getSyncEvents(vehicleId, appendParams);
    },
    [getSyncEvents],
  );

  const getSyncAllEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      return await doSyncEvents(vehicleId, params, 'all');
    },
    [doSyncEvents],
  );

  const getSyncFOAEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      return await doSyncEvents(vehicleId, params, 'foa_intervention');
    },
    [doSyncEvents],
  );

  const getSyncShutdownEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      return await doSyncEvents(vehicleId, params, 'shutdown');
    },
    [doSyncEvents],
  );

  const getSyncNetworkConnectionEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      return await doSyncEvents(vehicleId, params, 'network_connection');
    },
    [doSyncEvents],
  );

  const getSyncStuckAlarmEvents = useCallback(
    async (vehicleId: string, params: URLSearchParams) => {
      return await doSyncEvents(vehicleId, params, 'stuck_alarm');
    },
    [doSyncEvents],
  );

  return {
    loading,
    getSyncEvents,
    getSyncAllEvents,
    getSyncShutdownEvents,
    getSyncNetworkConnectionEvents,
    getSyncFOAEvents,
    getSyncStuckAlarmEvents,
  };
};
